import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { BASE_URL } from "../config/apiConfig";
// CHANGED: Removed `import { toast } from "react-toastify";` — this file only performs
// initial/background data fetching (fetchCalendar / refreshCalendar), which are not
// user-initiated actions (add/create/update/delete/save/login/etc.), so per requirements
// no toast notifications should fire from here. Import removed since it is now unused.

const CalendarContext = createContext();

export const useCalendar = () => useContext(CalendarContext);

export const CalendarProvider = ({ children }) => {
  // Raw API payload pieces (new backend shape)
  const [meta, setMeta] = useState(null);           // { month, year, days_in_month, today, current_user }
  const [users, setUsers] = useState({});            // { [userId]: { id, name, role, team, days: {date: {status, blocks}}, today_images } }
  const [employees, setEmployees] = useState([]);    // [{ id, name, role, team, is_manager }]
  const [team, setTeam] = useState([]);               // [{ id, name, role, team, status, checkin, checkout, hours }]
  const [holidays, setHolidays] = useState([]);       // [{ date: 'Y-m-d', name }]

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const token = localStorage.getItem("token");

  // ==============================
  // Fetch Attendance Calendar
  // ==============================
  const fetchCalendar = useCallback(async (year = "", month = "") => {
    try {
      setLoading(true);
      setError("");

      let url = `${BASE_URL}/attendance-calendar`;

      const params = [];
      if (year) params.push(`year=${year}`);
      if (month) params.push(`month=${month}`);
      if (params.length > 0) url += `?${params.join("&")}`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (res.status === 401) {
        // CHANGED: Removed toast.error("Session expired. Please login again.")
        // — this is a background/initial fetch failure, not a user-initiated
        // action, so it should not surface a toast. Error state is still set
        // so the UI can show an inline message if desired.
        setError("Session expired. Please login again.");
        return;
      }

      // CHANGED: Safely parse JSON instead of calling res.json() directly.
      // Previously, if the backend returned an HTML error page (e.g. a 404/500
      // page, a proxy error page, or a missing endpoint), res.json() would throw
      // "Unexpected token '<', "<!DOCTYPE ..." is not valid JSON" and crash this
      // function. We now check res.ok / content-type and safely attempt to parse,
      // falling back gracefully instead of throwing.
      let data = null;
      const contentType = res.headers.get("content-type") || "";

      if (!res.ok) {
        // Non-2xx response (404/500/etc.) — don't attempt to parse as JSON,
        // just record a generic error without crashing or toasting.
        setError(`Failed to fetch calendar (status ${res.status})`);
        return;
      }

      if (contentType.includes("application/json")) {
        try {
          data = await res.json();
        } catch (parseErr) {
          console.error("Failed to parse calendar response JSON:", parseErr);
          setError("Failed to fetch calendar");
          return;
        }
      } else {
        // Response wasn't JSON (e.g. an HTML page was returned unexpectedly).
        console.error("Expected JSON but received content-type:", contentType);
        setError("Failed to fetch calendar");
        return;
      }

      if (data && data.status === 200) {
        setMeta(data.meta || null);
        setUsers(data.users || {});
        setEmployees(data.employees || []);
        setTeam(data.team || []);
        setHolidays(data.holidays || []);
      } else {
        // CHANGED: Removed toast.error(data.message || "Failed to fetch calendar")
        // — background fetch failure, no toast per requirements.
        setError((data && data.message) || "Failed to fetch calendar");
      }
    } catch (err) {
      // CHANGED: Removed toast.error("Error fetching calendar") for network
      // errors — background fetch failure, no toast per requirements.
      console.error(err);
      setError("Error fetching calendar");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // ==============================
  // Refresh Current Month
  // ==============================
  const refreshCalendar = useCallback(async () => {
    const today = new Date();
    await fetchCalendar(today.getFullYear(), String(today.getMonth() + 1).padStart(2, "0"));
  }, [fetchCalendar]);

  useEffect(() => {
    if (token) {
      refreshCalendar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ==============================
  // Block sanitization
  // ==============================
  // FIXED: The backend can return blocks with missing/invalid `start` or
  // `end` time strings (e.g. an in-progress check-in with no check-out yet,
  // or a malformed record). Every consumer of blocksFor() eventually calls
  // b.start.split(":") / b.end.split(":") (in deriveDayData, toTop, toHeight,
  // to12h, diffLabel, etc.), so a single bad block anywhere would throw
  // "Cannot read properties of undefined (reading 'split')" and crash the
  // whole calendar view. We now validate and drop malformed blocks once,
  // centrally, right where blocks leave the context — so every downstream
  // view (Week/Day/Month, sidebar summary, timesheet card) always receives
  // clean data and callers don't each need their own guards.
  const isValidTimeStr = (t) => typeof t === "string" && /^\d{1,2}:\d{2}(:\d{2})?$/.test(t);

  const sanitizeBlocks = useCallback((blocks) => {
    if (!Array.isArray(blocks)) return [];
    const bad = [];
    const clean = blocks.filter((b) => {
      const ok = b && isValidTimeStr(b.start) && isValidTimeStr(b.end);
      if (!ok) bad.push(b);
      return ok;
    });
    if (bad.length) {
      // Kept intentionally (not removed alongside the toast cleanup above)
      // since this is a dev-facing diagnostic, not a user-facing notification.
      // It surfaces exactly which block(s) the backend sent without valid
      // start/end so the root cause (API bug vs. legitimate "in progress"
      // state) can be tracked down.
      console.warn("Dropping malformed calendar block(s) missing/invalid start or end:", bad);
    }
    return clean;
  }, []);

  // ==============================
  // Derived lookup helpers
  // ==============================
  // These give components the same call signature the old mock helpers
  // used (blocksFor(date), dayStatusFor(date)) but scoped to a userId,
  // since the API now returns data per-employee rather than one global set.

  const isoOf = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const dayEntryFor = useCallback(
    (date, userId) => {
      const user = users[userId];
      if (!user) return null;
      return user.days?.[isoOf(date)] || null;
    },
    [users]
  );

  const blocksFor = useCallback(
    (date, userId) => {
      const entry = dayEntryFor(date, userId);
      return sanitizeBlocks(entry ? entry.blocks || [] : []);
    },
    [dayEntryFor, sanitizeBlocks]
  );

  // ==============================
// Effective status (with "absent after cutoff" rule for today)
// ==============================
// The backend's `team` array ships a status per member, but that status
// can say "present" even when no check-in has happened yet today (it's
// just a default/placeholder). This recomputes status from the real
// per-day record, and — only for *today* — flips it to "absent" once
// it's past ABSENT_CUTOFF_HOUR and the person still has no check-in.
const ABSENT_CUTOFF_HOUR = 16; // 4 PM

const isoToday = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const effectiveStatusFor = useCallback(
  (userId) => {
    const user = users[userId];
    const todayIso = meta?.today || isoToday();
    const entry = user?.days?.[todayIso];

    // No record at all for today.
    if (!entry) {
      const hour = new Date().getHours();
      return hour >= ABSENT_CUTOFF_HOUR ? "absent" : null;
    }

    if (entry.status === "weekend") return "weekend";
    if (entry.status === "holiday")  return "holiday";
    if (entry.status === "leave")    return "leave";
    if (entry.status === "absent")   return "absent";
    if (entry.status === "half_day") return "half";
    if (entry.status === "late")     return "late";
    if (entry.status === "present")  return "present";

    // entry.status === "" (blank) -> check-in is what actually decides it.
    // NOTE: uses the raw entry.blocks (not sanitizeBlocks) since we only
    // need to know whether *any* checkin block exists, not its times.
    const hasCheckin = (entry.blocks || []).some((b) => b?.type === "checkin");
    if (hasCheckin) return "present";

    const hour = new Date().getHours();
    return hour >= ABSENT_CUTOFF_HOUR ? "absent" : null;
  },
  [users, meta]
);

  // Normalises backend status strings ('half_day') to the frontend's
  // existing STATUS_META keys ('half'). Weekend / blank -> null, matching
  // the old dayStatusFor() contract (null = "no badge, treat specially").
  const dayStatusFor = useCallback(
    (date, userId) => {
      const entry = dayEntryFor(date, userId);
      if (!entry) return null;
      if (entry.status === "weekend" || entry.status === "") return null;
      if (entry.status === "half_day") return "half";
      return entry.status; // 'present' | 'late' | 'leave' | 'holiday' | 'absent'
    },
    [dayEntryFor]
  );

  const isWeekendDateFor = useCallback(
    (date, userId) => {
      const entry = dayEntryFor(date, userId);
      return entry ? entry.status === "weekend" : false;
    },
    [dayEntryFor]
  );

  const isLeaveDateFor = useCallback(
    (date, userId) => dayStatusFor(date, userId) === "leave",
    [dayStatusFor]
  );

  const todayImagesFor = useCallback(
    (userId) => users[userId]?.today_images || null,
    [users]
  );

  const graceInfoFor = useCallback(
    (userId) => {
        const user = users[userId];

        if (!user) {
        return {
            allowed: 0,
            used: 0,
            status: "On Time",
        };
        }

        return {
        allowed: user.grace_allowed || 0,
        used: user.grace_used || 0,
        status:
            (user.grace_used || 0) > 0
            ? "Grace Used"
            : "On Time",
        };
    },
    [users]
    );

  const holidayMap = useMemo(() => {
    const map = {};
    holidays.forEach((h) => {
      // key as d-m-Y to match what was previously used in mock helpers
      const [y, m, d] = h.date.split("-");
      map[`${d}-${m}-${y}`] = h.name;
    });
    return map;
  }, [holidays]);

  const currentUserId = meta?.current_user?.id ?? null;

  return (
    <CalendarContext.Provider
      value={{
        // raw state
        meta,
        users,
        employees,
        team,
        holidays,
        holidayMap,
        currentUserId,
        loading,
        error,

        // actions
        fetchCalendar,
        refreshCalendar,

        // per-user lookup helpers (mirror old mock helper signatures + userId)
        blocksFor,
        dayStatusFor,
        isWeekendDateFor,
        isLeaveDateFor,
        todayImagesFor,
        graceInfoFor,
        effectiveStatusFor,
      }}
    >
      {children}
    </CalendarContext.Provider>
  );
};